import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Users, Plus, X, Trash2, KeyRound } from "lucide-react";
import { toast } from "sonner";

export default function PortalUsers() {
    const [list, setList] = useState([]);
    const [clients, setClients] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ email: "", name: "", password: "", client_id: "", phone: "" });

    const load = async () => {
        try {
            const [u, c] = await Promise.all([api.get("/admin/portal-users"), api.get("/clients")]);
            if (u?.data) setList(u.data);
            if (c?.data) setClients(c.data);
        } catch (e) { console.warn(e); }
    };
    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault();
        try {
            await api.post("/admin/portal-users", form);
            toast.success("Usuario de portal creado. Compártele las credenciales.");
            setOpen(false);
            setForm({ email: "", name: "", password: "", client_id: "", phone: "" });
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const remove = async (id) => {
        if (!window.confirm("¿Eliminar este usuario de portal?")) return;
        try {
            await api.delete(`/admin/portal-users/${id}`);
            toast.success("Usuario eliminado");
            load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const randomPw = () => setForm({ ...form, password: "FM" + Math.random().toString(36).slice(2, 10) + "!" });

    return (
        <div className="space-y-4" data-testid="portal-users-page">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="font-display font-bold text-lg">Usuarios de Portal Cliente</h2>
                    <div className="text-sm text-slate-500">{list.length} usuario(s) con acceso al portal corporativo</div>
                </div>
                <button onClick={() => setOpen(true)} className="fm-btn-accent" data-testid="new-portal-user-btn">
                    <Plus className="w-3.5 h-3.5" /> Crear acceso a portal
                </button>
            </div>

            <div className="fm-card overflow-hidden">
                <table className="w-full text-sm">
                    <thead><tr className="bg-slate-50 border-b border-slate-200">
                        {["Nombre", "Email", "Cliente", "Teléfono", "Creado", ""].map((h) => (
                            <th key={h} className="text-left py-2.5 px-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">{h}</th>
                        ))}
                    </tr></thead>
                    <tbody>
                        {list.map((u) => (
                            <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50">
                                <td className="py-2 px-3 font-medium">{u.name}</td>
                                <td className="py-2 px-3 font-mono text-xs">{u.email}</td>
                                <td className="py-2 px-3">{u.client_name}</td>
                                <td className="py-2 px-3 font-mono text-xs">{u.phone || "—"}</td>
                                <td className="py-2 px-3 text-xs font-mono text-slate-500">{u.created_at?.slice(0, 10)}</td>
                                <td className="py-2 px-3">
                                    <button onClick={() => remove(u.id)} className="text-red-500 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {list.length === 0 && (
                    <div className="p-10 text-center text-sm text-slate-500">
                        <Users className="w-10 h-10 mx-auto mb-2 text-slate-300" strokeWidth={1} />
                        Aún no hay usuarios de portal. Crea credenciales para que tus clientes corporativos accedan a su panel.
                    </div>
                )}
            </div>

            {open && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-sm max-w-lg w-full">
                        <div className="flex items-center justify-between p-4 border-b border-slate-200">
                            <h3 className="font-display font-bold">Crear acceso a portal</h3>
                            <button onClick={() => setOpen(false)}><X className="w-5 h-5" /></button>
                        </div>
                        <form onSubmit={submit} className="p-5 space-y-3">
                            <div>
                                <label className="fm-label">Cliente *</label>
                                <select required value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} className="fm-input">
                                    <option value="">Seleccionar cliente</option>
                                    {clients.map((c) => <option key={c.id} value={c.id}>{c.business_name}</option>)}
                                </select>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div><label className="fm-label">Nombre *</label><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="fm-input" /></div>
                                <div><label className="fm-label">Teléfono</label><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="fm-input" /></div>
                                <div className="col-span-2"><label className="fm-label">Email *</label><input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="fm-input" /></div>
                                <div className="col-span-2">
                                    <label className="fm-label">Contraseña *</label>
                                    <div className="flex gap-2">
                                        <input required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="fm-input flex-1" />
                                        <button type="button" onClick={randomPw} className="fm-btn-outline whitespace-nowrap"><KeyRound className="w-3.5 h-3.5" /> Generar</button>
                                    </div>
                                </div>
                            </div>
                            <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
                                <button type="button" onClick={() => setOpen(false)} className="fm-btn-outline">Cancelar</button>
                                <button type="submit" className="fm-btn-accent">Crear acceso</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
